(ns app.zap-vault.login.view
  (:require [app.zap-vault.event :as app-event]
            [app.zap-vault.login.event :as event]
            [app.zap-vault.login.model :as model]
            [app.zap-vault.login.sub :as sub]
            [app.zap-vault.sub :as app-sub]
            [common.localization :as loc]
            [common.util :as cu]
            [re-frame.core :as rf]
            [ui.components.button.view :as button]
            [ui.components.layout.footer :as footer]
            [ui.components.link.view :as link]
            [ui.components.modal.style :as modal-style]
            [ui.components.modal.view :as modal]
            [ui.components.rich-text.tiptap :as tiptap]
            [ui.dom-globals :as dg]
            [ui.editors.data-input :as data-input]
            [ui.editors.labelled-input.view :as labelled-input]
            [ui.layout :as layout]
            [ui.typography :as t]
            [ui.util :as uu]))

(defn disclaimer-modal
  []
  (let [visible? @(rf/subscribe [::sub/disclaimer-visible?])
        vault-config @(rf/subscribe [::app-sub/vault-config])
        checked? @(rf/subscribe [::sub/confirmed?])]
    [modal/view
     {:visible? visible?
      :size     ::modal-style/md}
     (loc/tr :disclaimer)
     [:div
      {:class "flex-auto flex flex-col gap-4"}
      [tiptap/viewer
       {:value (:vault-config/disclaimer-message vault-config)
        :styles {:backgroundColor "transparent"
                 :border "none"}}]

      [:div
       {:class "p-2 bg-base-regular text-base-regular-text rounded border border-1 border-base-regular-border-paler"}
       [:label
        {:class "text-n-xs text-input-light-text font-semibold flex flex-row gap-2 items-center"}
        [data-input/view
         {:editor-type :editor.type/checkbox
          :value       checked?
          :on-change   (fn [v]
                         (rf/dispatch [::event/set-confirmed? v]))}]
        (:vault-config/disclaimer-confirm-text vault-config)]]
      [footer/footer
       [footer/left-align]
       [footer/right-align
        [button/secondary-hollow
         {:on-click (fn [_]
                      (rf/dispatch [::app-event/logout]))}
         (loc/tr :exit)]
        [button/primary-solid
         {:on-click  (fn [_]
                       (rf/dispatch [::event/submit-confirmation]))
          :disabled? (not checked?)}
         (loc/tr :continue)]]]]]))

(defn step-circle-completed
  [button-color text-color]
  [:div
   {:class "w-[16px] @md:w-[34px] h-[16px] @md:h-[34px] rounded-full grid place-items-center"
    :style {:background button-color
            :color      text-color}}
   [:i
    {:class "text-xs fa fal fa-check"}]])

(defn step-circle-active
  [button-color _text-color]
  [:div
   {:class (uu/cm "w-2 h-2 @md:w-[16px] @md:h-[16px] rounded-full grid place-items-center ring-[4px] mx-1 @md:ring-[9px] @md:my-[9px]")
    :style {"--tw-ring-color" button-color}}])

(defn step-circle-inactive
  []
  [:div
   {:class "w-[16px] @md:w-[34px] h-[16px] @md:h-[34px] rounded-full grid place-items-center bg-disabled-light"}])

(defn vault-info
  []
  (let [step @(rf/subscribe [::sub/step])
        step-1? (= step model/step-1)
        vault-config @(rf/subscribe [::app-sub/vault-config])
        vault-details @(rf/subscribe [::app-sub/vault-details])
        {:keys [vault-name vault-description]} vault-details
        {:keys [vault-config/vault-logo vault-config/button-color vault-config/text-color]} vault-config]
    [:div {:class "flex-auto flex flex-col justify-start gap-2 p-4 @md:gap-6"}
     [:div {:class "flex-0 flex flex-row @md:flex-col items-start gap-6"}
      [:img
       {:src   vault-logo
        :class ""}]
      [:div
       {:class "flex-auto flex flex-row justify-end md:justify-start"}
       [t/h1 {}
        (str vault-name)]]]
     [:div
      {:class "[&_.ProseMirror]:text-n-2xs [&_.ProseMirror]:border-0 [&_.ProseMirror]:bg-transparent"}
      [tiptap/viewer
       {:value vault-description
        :styles {:background "transparent"
                 :border "none"
                 :padding "0"}}]]

     ;; left rail: circles + connector
     [:div {:class "flex-0 flex flex-col @md:flex-row @md:flex-auto gap-0 @md:gap-2 justify-items-center"}
      ;; icon rail
      [:div {:class "flex flex-row @md:flex-col items-center pt-2 gap-3 @md:gap-0"}
       ;; step 1 circle
       [:div
        {:class "flex-0 flex flex-row"}
        (if step-1?
          [step-circle-active button-color text-color]
          [step-circle-completed button-color text-color])]
       ;; connector line
       [:div {:class (uu/cm "flex-auto h-[1px] w-full @md:h-auto @md:w-[1px] @md:h-full @md:min-h-12 my-2 @md:my-[2px]"
                            (when step-1?
                              "bg-disabled-light"))
              :style {:background-color (when-not step-1?
                                          button-color)}}]
       ;; step 2 circle
       [:div
        {:class "flex-0 flex flex-row"}
        (if step-1?
          [step-circle-inactive button-color text-color]
          [step-circle-active button-color text-color])]]
      ;; labels
      [:div
       {:class "flex flex-row @md:flex-col items-center @md:items-start pt-0 @md:pt-2 gap-3"}
       ;; step 1 labels
       [:div
        {:class "flex flex-col flex-auto justify-start items-start"}
        [:div {:class "text-n-xs font-n-bold"}
         (loc/tr :vault-login-step-1)]
        [:div {:class "text-n-xs font-n-semi"}
         (loc/tr :vault-login-step-1-info)]]
       ;; step 2 labels
       [:div
        {:class "flex flex-col flex-auto items-end @md:items-start @md:mt-[62px]"}
        [:div {:class (uu/cm "text-n-xs font-n-bold"
                             (when step-1?
                               "text-gray-400"))}
         (loc/tr :vault-login-step-2)]
        [:div {:class (uu/cm "text-n-xs font-n-semi"
                             (when step-1?
                               "text-gray-400"))}
         (loc/tr :vault-login-step-2-info)]]]]
     [:div
      {:class "hidden h-0
          @md:flex @md:flex-col @md:h-full flex-auto"}]]))

(defn email-form
  []
  (let [email @(rf/subscribe [::sub/email])
        valid-email? @(rf/subscribe [::sub/valid-email?])
        vault-config @(rf/subscribe [::app-sub/vault-config])
        {:keys [vault-config/button-color vault-config/text-color]} vault-config]
    [:form {:class     "flex flex-col gap-1 w-full"
            :on-submit (fn [e]
                         (rf/dispatch [::event/request-code])
                         (dg/prevent-default e)
                         false)}

     [labelled-input/view
      {:label     (loc/tr :vault-login-email-field-label)
       :data-type :data.type/email
       :value     (or email "")
       :on-change (fn [v]
                    (rf/dispatch [::event/set-email v]))}]
     [:div
      {:class "flex-1 flex flex-row gap-2 justify-end items-start pt-2"}
      [button/primary-solid
       {:type      "submit"
        :style     {:background button-color
                    :color      text-color}
        :tooltip   (when-not valid-email?
                     (loc/tr :valid-email-required))
        :disabled? (not valid-email?)}
       (loc/tr :vault-login-request-code-button)]]]))

(defn format-until-ms
  [ts]
  (let [seconds (/ ts 1000)
        minutes (/ seconds 60)
        hours (/ minutes 60)
        days (/ hours 24)]
    (cond
      (> days 1) (loc/tr :in-x-days [(js/Math.round days)])
      (> hours 1) (loc/tr :in-x-hours [(js/Math.round hours)])
      (> minutes 1) (loc/tr :in-x-minutes [(js/Math.round minutes)])
      :else (loc/tr :in-x-seconds [(js/Math.round seconds)]))))

(defn code-form
  []
  (let [email @(rf/subscribe [::sub/email])
        vault-config @(rf/subscribe [::app-sub/vault-config])
        {:keys [vault-config/button-color vault-config/text-color]} vault-config
        auth-code @(rf/subscribe [::sub/auth-code])
        blank-code? (cu/blank-value? auth-code)
        error-message @(rf/subscribe [::sub/code-error-message])
        blocked-until-in-ms @(rf/subscribe [::sub/blocked-until-in-ms])
        can-resend-in-ms @(rf/subscribe [::sub/can-resend-in-ms])
        tries-left @(rf/subscribe [::sub/tries-left])]
    [:form {:class     "flex flex-col gap-1 w-full"
            :on-submit (fn [e]
                         (rf/dispatch [::event/send-code])
                         (dg/prevent-default e)
                         false)}
     [:<>
      [labelled-input/view
       {:label     [:div
                    {:class "flex flex-row gap-2"}
                    (loc/tr :vault-login-auth-code-field-label [email])
                    [:div
                     {:class    (button/link-style nil)
                      :on-click (fn [_]
                                  (rf/dispatch [::event/reset]))}
                     (loc/tr :change)]]
        :disabled? (pos? blocked-until-in-ms)
        :data-type :data.type/text-simple
        :error     error-message
        :value     (or auth-code "")
        :on-change (fn [v]
                     (rf/dispatch [::event/set-auth-code v]))}]
      [layout/row
       [layout/left-align
        (when (or (pos? blocked-until-in-ms)
                  (not (cu/blank-value? error-message)))
          [:div
           {:class (uu/cm "text-[12px] text-danger-light-text-accent")}
           (if (pos? blocked-until-in-ms)
             (loc/tr :too-many-tries-to-login-try-again-in [(format-until-ms blocked-until-in-ms)])
             (str error-message " "
                  (loc/tr :tries-left [tries-left])))])]
       (if (number? can-resend-in-ms)
         [layout/right-align
          [link/view
           {:class    "text-[12px] cursor-not-allowed"
            :on-click (fn [e]
                        (dg/prevent-and-stop e)
                        false)
            :url      "#"
            :label    (loc/tr :did-not-receive-email?send-again-in [(format-until-ms can-resend-in-ms)])}]
          [:div
           {:class (uu/cm "text-n-2xs text-")}
           ()]]
         [layout/right-align
          (when-not (pos? blocked-until-in-ms)
            [:div
             {:class    (uu/cm "text-[12px]" (button/link-style nil))
              :on-click (fn [_]
                          (rf/dispatch [::event/request-code]))}
             (loc/tr :resend-code)])])]

      [:div
       {:class "flex-1 flex flex-row gap-2 justify-end items-start pt-2"}
       [button/primary-solid
        {:type      "submit"
         :style     {:background button-color
                     :color      text-color}
         :tooltip   (when blank-code?
                      (loc/tr :enter-code-received-via-email))
         :disabled? (or blank-code?
                        (pos? blocked-until-in-ms))}
        (loc/tr :vault-login-auth-code-button)]]]]))

(defn login-screen []
  ;; Page wrapper: stacked on mobile, split columns & full-height on md+
  (let [email-sent? @(rf/subscribe [::sub/email-sent-ok?])]
    [layout/inline-container
     [layout/sidebar-main {}
      [:div
       {:class "bg-base-light pt-0 @md:pt-[100px] flex flex-0 @md:flex-auto"}
       [:div
        {:class "flex-auto flex flex-row justify-self-center mx-0 @md:mx-12"}
        [vault-info]]]
      [:div
       {:class "bg-base-regular p-4 @md:pt-[177px] flex flex-auto"}
       [:div
        {:class "flex-auto flex flex-row justify-self-start mx-0 @md:mx-12"}
        (if email-sent?
          [code-form]
          [email-form])]]]
     [disclaimer-modal]]))


(defn view
  []
  [login-screen])
